import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Button, TextField } from '@mui/material';
import { Close, Save, Delete, Edit } from '@mui/icons-material';
import { useSymbol } from '@/contexts/SymbolContext';
import SymbolSelector from '../symbolSelector';
import { PlatformId, SetRiskContractLimitModel } from '@/api/userApi';
import HelpMessage from '../helpMessage';
import { useTradingAccount } from '@/contexts/TradingAccountContext';
import { TradingEnvironment } from '@/data/tradingEnvironment';

interface ContractLimitsProps {
  limits: SetRiskContractLimitModel[];
  onChange?: (limits: SetRiskContractLimitModel[]) => void;
  resetInputsKey?: number;
}

const ContractLimits: React.FC<ContractLimitsProps> = ({ limits, onChange, resetInputsKey }) => {
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [limitValue, setLimitValue] = useState<string>('');
  const [globalLimitValue, setGlobalLimitValue] = useState<string>('');
  const [editingSymbolId, setEditingSymbolId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const { activeTradingAccount, tradingEnvironment } = useTradingAccount();

  const { getContractByProductId, getContractByProductName } = useSymbol();

  // Find existing global limit
  const existingGlobalLimit = limits.find((l) => l.symbolId === 'GLOBAL');
  const isFollower = activeTradingAccount.isFollower;

  useEffect(() => {
    setSelectedSymbol(null);
    setLimitValue('');
    setGlobalLimitValue('');
    setEditingSymbolId(null);
    setEditingValue('');
  }, [resetInputsKey]);

  // Keep globalLimitValue in sync with prop
  useEffect(() => {
    if (existingGlobalLimit) {
      setGlobalLimitValue(existingGlobalLimit.limit.toString());
    } else {
      setGlobalLimitValue('');
    }
  }, [existingGlobalLimit?.limit, limits]);

  // Only allow non-negative integers (or empty string)
  const handleLimitInput = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && Number(value) >= 0)) {
      setter(value);
    }
  };

  const addContractLimit = () => {
    if (!selectedSymbol || limitValue === '' || !onChange) return;

    const contract = getContractByProductName(selectedSymbol);
    if (!contract) return;

    if (limits.some((l) => l.symbolId === contract.productId)) {
      return;
    }

    onChange([
      ...limits,
      {
        symbolId: contract.productId,
        limit: Number(limitValue)
      } as SetRiskContractLimitModel
    ]);
    setSelectedSymbol(null);
    setLimitValue('');
  };

  const saveGlobalLimit = () => {
    if (globalLimitValue === '' || !onChange) return;

    const otherLimits = limits.filter((l) => l.symbolId !== 'GLOBAL');
    onChange([
      ...otherLimits,
      {
        symbolId: 'GLOBAL',
        limit: Number(globalLimitValue)
      } as SetRiskContractLimitModel
    ]);
  };

  const deleteGlobalLimit = () => {
    if (!onChange) return;
    onChange(limits.filter((l) => l.symbolId !== 'GLOBAL'));
    setGlobalLimitValue('');
  };

  const removeContractLimit = (symbolId: string) => {
    if (!onChange) return;
    onChange(limits.filter((l) => l.symbolId !== symbolId));
  };

  const startEdit = (symbolId: string, value: number) => {
    setEditingSymbolId(symbolId);
    setEditingValue(value.toString());
  };

  const cancelEdit = () => {
    setEditingSymbolId(null);
    setEditingValue('');
  };

  const saveEdit = (symbolId: string) => {
    if (!onChange || editingValue === '' || !/^\d+$/.test(editingValue)) return;
    onChange(limits.map((l) => (l.symbolId === symbolId ? { ...l, limit: Number(editingValue) } : l)));
    setEditingSymbolId(null);
    setEditingValue('');
  };

  const getSymbolName = (symbolId: string) => {
    if (symbolId === 'GLOBAL') {
      return <span style={{ fontWeight: 'bold', color: '#4caf50' }}>ACCOUNT</span>;
    }

    const contract = getContractByProductId(symbolId);
    if (!contract) {
      return 'Unknown';
    }

    return (
      <span>
        {contract.productName}
        <span style={{ color: 'grey', marginLeft: '1em' }}>({contract.description})</span>
      </span>
    );
  };

  const selector = useMemo(() => {
    return <SymbolSelector selectedSymbol={selectedSymbol} onSymbolChange={setSelectedSymbol} doNotInclude={limits.map((l) => l.symbolId).filter((id) => id !== 'GLOBAL')} />;
  }, [selectedSymbol, limits]);

  // Sort limits to show GLOBAL first
  const sortedLimits = useMemo(() => {
    return [...limits].sort((a, b) => {
      if (a.symbolId === 'GLOBAL') return -1;
      if (b.symbolId === 'GLOBAL') return 1;
      return 0;
    });
  }, [limits]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      {isFollower && (
        <Typography variant='caption' color='error' sx={{ display: 'block', mt: 1, fontSize: '1.2em' }}>
          Contract limits are disabled for follower accounts.
        </Typography>
      )}

      {!isFollower && (
        <>
          {tradingEnvironment !== TradingEnvironment.Live && activeTradingAccount.maxMargin && (
            <Typography variant='body1' style={{ color: '#fff', marginTop: 2 }}>
              [{activeTradingAccount.accountName}] max position size is {Math.round(activeTradingAccount.maxMargin / 10000)} minis / {Math.round(activeTradingAccount.maxMargin / 1000)} micros
            </Typography>
          )}
          <Card variant='outlined' sx={{ mb: 0 }}>
            <CardContent sx={{ padding: '1.2em', paddingBottom: '1em !important' }}>
              <Typography color='white' variant='h6' sx={{ mb: 2, fontWeight: 'bold' }}>
                Symbol Contract Limits
                <HelpMessage message='Sets a specific contract limit for an individual symbol.' />
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexDirection: 'column',
                  width: '100%'
                }}
              >
                <Box sx={{ flexGrow: 1, width: '100%' }}>{selector}</Box>
                <TextField
                  label='Limit'
                  type='text'
                  value={limitValue}
                  onChange={handleLimitInput(setLimitValue)}
                  fullWidth
                  inputProps={{
                    min: 0,
                    step: 1,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 8
                  }}
                />
                <Button style={{ width: '100%' }} variant='contained' color='primary' onClick={addContractLimit} disabled={!selectedSymbol || limitValue === ''}>
                  Add Limit
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Typography variant='subtitle1' style={{ color: '#fff', marginTop: 2 }}>
            Current Contract Limits
          </Typography>

          {limits.length === 0 ? (
            <Card variant='outlined'>
              <CardContent sx={{ padding: '1em', textAlign: 'center' }}>
                <Typography color='textSecondary'>No contract limits set</Typography>
              </CardContent>
            </Card>
          ) : (
            <List sx={{ maxHeight: '200px', overflow: 'auto' }}>
              {sortedLimits.map(({ symbolId, limit }) => (
                <Card key={symbolId} variant='outlined' sx={{ mb: 1 }}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <>
                          {getSymbolName(symbolId)}{' '}
                          {editingSymbolId === symbolId ? (
                            <>
                              <TextField
                                value={editingValue}
                                onChange={handleLimitInput(setEditingValue)}
                                type='text'
                                size='small'
                                inputProps={{
                                  min: 0,
                                  step: 1,
                                  inputMode: 'numeric',
                                  pattern: '[0-9]*',
                                  maxLength: 8,
                                  style: { width: 80 }
                                }}
                                sx={{ ml: 2, width: 100 }}
                              />
                              <IconButton color='primary' onClick={() => saveEdit(symbolId)} disabled={editingValue === ''} title='Save'>
                                <Save />
                              </IconButton>
                              <IconButton color='error' onClick={cancelEdit} title='Cancel'>
                                <Close />
                              </IconButton>
                            </>
                          ) : (
                            <span style={{ marginLeft: '1em' }}>Limit: {limit}</span>
                          )}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      {editingSymbolId !== symbolId && (
                        <>
                          <IconButton edge='end' aria-label='edit' onClick={() => startEdit(symbolId, limit)} title='Edit'>
                            <Edit />
                          </IconButton>
                          <IconButton edge='end' aria-label='delete' onClick={() => removeContractLimit(symbolId)} title='Remove'>
                            <Close />
                          </IconButton>
                        </>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                </Card>
              ))}
            </List>
          )}
        </>
      )}
    </Box>
  );
};

export default ContractLimits;
